import { startCase } from "lodash";
import { FC, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import { ErrorsDisplayContainer } from "@ryde/controls";
import { useCrudEditContext } from "@ryde/redux";
import {
  canResolveAlways,
  EditFormMode,
  errorsCrudContentAtomFamily,
  formTabControlsSF,
  IExtendableDto,
  IInjectedDrawerProps,
  IJourneyEditDrawerProps,
  RIGHTBAR_JOURNEYS_WIDTH,
  useHasPermissions,
} from "@ryde/shared";

import { FormControlsWrapper, useEditFormInner } from "../../../layout-edit";
import { EditFormTabs } from "../../../layout-edit/edit-form.tabs";
import {
  BaseDrawer,
  DrawerContentStyled,
  DrawerFormContentGate,
  DrawerHeader,
  FormFooter,
} from "../drawer";
import { useEditFormDrawerLoading } from "../edit-form";
import { JourneyCrudEditFormFooter } from "./journey-crud-edit-form-footer";

export const JourneyDrawerEditFormInner: FC<
  IJourneyEditDrawerProps & IInjectedDrawerProps
> = ({
  onCloseDrawer,
  resolveFn,
  drawerNumber,
  subTitle,
  mode,
  canResolve = canResolveAlways,
}) => {
  const {
    data: { isEdit, isDuplicate, dto },
    entity,
    id,
  } = useCrudEditContext<IExtendableDto>();

  const {
    onArchiveClick,
    closeSidebar: onCloseHandler,
    onSaveClick,
  } = useEditFormInner({ resolveFn, canResolve, onCloseDrawer });
  const isAddNew = !isEdit;

  const {
    formState: { isDirty, isValid, errors },
  } = useFormContext();
  const { t } = useTranslation();
  const { isFormContentLoading, isFormFooterLoading } =
    useEditFormDrawerLoading({
      entity,
      requestedId: id,
      dtoId: dto?.id,
      hasDto: Boolean(dto),
    });
  const serverErrors = useRecoilValue(errorsCrudContentAtomFamily(entity));
  const currentTabControls = useRecoilValue(formTabControlsSF(entity));

  const unitTitle = t(`${entity}.single`);

  // Generate title: "Entity: Name" for edit, "Add new Entity" for adding.
  const title = useMemo(() => {
    if (isEdit) {
      if (dto) {
        const entityName = dto.title || dto.name || "";
        const baseTitle = entityName
          ? `${unitTitle}: ${entityName}`
          : unitTitle;
        return startCase(baseTitle);
      }
      return startCase(unitTitle);
    }
    return startCase(t(`general.ADD_NEW`, { entity: unitTitle }));
  }, [isEdit, dto, unitTitle, t]);
  const { hasPermissions: hasUpdatePermissions } = useHasPermissions({
    entity,
    action: "update",
  });

  const isSaveDisabled =
    !isValid || (!isDirty && !isDuplicate) || (isEdit && !hasUpdatePermissions);

  return (
    <BaseDrawer
      onCloseDrawer={onCloseHandler}
      drawerNumber={drawerNumber || 0}
      customWidth={RIGHTBAR_JOURNEYS_WIDTH}
    >
      <DrawerFormContentGate
        isLoading={isFormContentLoading}
        entityTitle={unitTitle}
      >
        <DrawerHeader
          onCloseDrawer={onCloseHandler}
          title={title}
          subTitle={subTitle}
          drawerNumber={drawerNumber}
        />
        <DrawerContentStyled className="drawer-form-with-image">
          <EditFormTabs>
            <ErrorsDisplayContainer
              serverErrors={serverErrors}
              formErrors={isValid ? undefined : errors}
            />
            {(isAddNew || !!dto) && (
              <FormControlsWrapper controls={currentTabControls} />
            )}
          </EditFormTabs>
        </DrawerContentStyled>
        {mode !== EditFormMode.CrudRoute && (
          <FormFooter
            entity={entity}
            onSaveClick={onSaveClick}
            onCancelClick={onCloseHandler}
            isLoading={isFormFooterLoading}
            isSaveDisabled={isSaveDisabled}
          />
        )}
        {mode === EditFormMode.CrudRoute && (
          <JourneyCrudEditFormFooter
            entity={entity}
            onSaveClick={onSaveClick}
            onCancelClick={onCloseHandler}
            archiveClick={onArchiveClick}
            isLoading={isFormFooterLoading}
            isSaveDisabled={isSaveDisabled}
            isEdit={isEdit}
          />
        )}
      </DrawerFormContentGate>
    </BaseDrawer>
  );
};
